import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useUserFormularios() {
  const queryClient = useQueryClient();

  // Buscar formulários do usuário
  const { data: notificacoes, isLoading } = useQuery({
    queryKey: ['user-formularios'],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Usuário não autenticado');

      const { data, error } = await supabase
        .from('formulario_notificacoes')
        .select(`
          *,
          formulario:formularios(
            id,
            titulo,
            descricao,
            categoria,
            total_respostas
          ),
          distribuicao:formulario_distribuicoes(
            id,
            obrigatorio,
            data_limite,
            mensagem_personalizada
          ),
          resposta:formulario_respostas(
            id,
            status,
            data_conclusao
          )
        `)
        .eq('usuario_id', userData.user.id)
        .order('criado_em', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Separar notificações por status
  const formulariosPendentes = notificacoes?.filter(n => 
    !n.respondida && 
    (!n.distribuicao?.data_limite || new Date(n.distribuicao.data_limite) > new Date())
  ) || [];

  const formulariosRespondidos = notificacoes?.filter(n => n.respondida) || [];

  const formulariosVencidos = notificacoes?.filter(n => 
    !n.respondida && 
    n.distribuicao?.data_limite && 
    new Date(n.distribuicao.data_limite) < new Date()
  ) || [];

  const totalPendentes = formulariosPendentes.length;

  // Marcar como lida
  const { mutateAsync: marcarComoLido } = useMutation({
    mutationFn: async (notificacaoId: string) => {
      const { error } = await supabase
        .from('formulario_notificacoes')
        .update({ 
          lida: true,
          lida_em: new Date().toISOString()
        })
        .eq('id', notificacaoId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-formularios'] });
    },
    onError: (error: any) => {
      console.error('Erro ao marcar como lido:', error);
    },
  });

  // Verificar se pode responder (não vencido)
  const podeResponder = (notificacao: any) => {
    if (notificacao.respondida) return false;
    
    const dataLimite = notificacao.distribuicao?.data_limite;
    if (!dataLimite) return true;
    
    return new Date(dataLimite) > new Date();
  };

  // Criar resposta para formulário distribuído
  const { mutateAsync: iniciarResposta, isPending: isIniciando } = useMutation({
    mutationFn: async (params: { notificacaoId: string; formularioId: string }) => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Usuário não autenticado');

      // Buscar client_id do usuário
      const { data: userProfile } = await supabase
        .from('users')
        .select('client_id')
        .eq('id', userData.user.id)
        .single();

      // Criar resposta como rascunho
      const { data: resposta, error: respostaError } = await supabase
        .from('formulario_respostas')
        .insert({
          formulario_id: params.formularioId,
          respondido_por: userData.user.id,
          client_id: userProfile?.client_id,
          status: 'rascunho',
        })
        .select()
        .single();

      if (respostaError) throw respostaError;

      // Atualizar notificação com ID da resposta
      const { error: notifError } = await supabase
        .from('formulario_notificacoes')
        .update({ resposta_id: resposta.id })
        .eq('id', params.notificacaoId);

      if (notifError) throw notifError;

      return resposta;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-formularios'] });
    },
    onError: (error: any) => {
      console.error('Erro ao iniciar resposta:', error);
      toast.error('Erro ao iniciar resposta');
    },
  });

  // Marcar como respondida quando concluir
  const { mutateAsync: marcarComoRespondida } = useMutation({
    mutationFn: async (params: { notificacaoId: string; respostaId: string }) => {
      const { error } = await supabase
        .from('formulario_notificacoes')
        .update({ 
          respondida: true,
          resposta_id: params.respostaId
        })
        .eq('id', params.notificacaoId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-formularios'] });
      toast.success('Formulário respondido com sucesso!');
    },
    onError: (error: any) => {
      console.error('Erro ao marcar como respondida:', error);
    },
  });

  return {
    formulariosPendentes,
    formulariosRespondidos,
    formulariosVencidos,
    totalPendentes,
    isLoading,
    marcarComoLido,
    podeResponder,
    iniciarResposta,
    isIniciando,
    marcarComoRespondida,
  };
}
